import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { SEE_ITEM_COST } from './permissions.js';
import { IsoDateSchema, MessageSchema } from './primitives.js';
import { AdjustmentReasonSchema } from './stock-adjustments.js';
const c = initContract();
/**
 * ADM-03 — THE IMPORT / EXPORT CENTRE (spec §15.8).
 *
 * "This screen lets you move a lot of records in or out at once, using a
 * spreadsheet. You use it when you first set PalAccount up and need to load a
 * thousand items, or when your accountant asks for everything in a file."
 *
 * ══════════════════════════════════════════════════════════════════════════
 * BOTH HALVES ARE BUILT. The import arrived in its own wave; this is its
 * contract, and the paragraphs below are the ones the export half wrote
 * scoping it, kept because they are still the specification.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * §10 gives the import five rules and every one of them is a real piece of
 * engineering:
 *
 *   BR-ADM-03-01 — "An import must be CHECKED BEFORE ANYTHING IS WRITTEN. The
 *     system must show how many rows will be added, how many changed, and how
 *     many refused, and must WAIT for the user to say go." A dry run is not a
 *     validation pass; it is a full resolution of every row against every
 *     master record, held somewhere, replayed on confirmation.
 *   BR-ADM-03-02 — "all-or-nothing. A half-finished import must not be
 *     possible." Fifty thousand rows in one transaction, or a staging table.
 *   BR-ADM-03-03 — the refused rows, downloadable, with the reason on each.
 *   BR-ADM-03-04/-05 — never overwrite a confirmed document; opening balances
 *     only into an open period with no confirmed documents in it.
 *   §15's "two users at once" — two people importing the same type must be
 *     blocked, and the second told.
 *
 * And the rule under all of them, from §12: every imported row must satisfy
 * every rule a TYPED record satisfies — a partner still needs its tax-id
 * uniqueness, an item still needs a valid unit and an existing category
 * (§15's own example: "refuse the row and say which category was missing").
 * That means the import cannot bypass the services; it has to call them, which
 * is where the real work is.
 *
 * Shipping half of that is worse than shipping none: a half-applied import is
 * precisely BR-ADM-03-02's prohibition, and it is the one failure on this
 * screen that a user cannot undo.
 *
 * ── HOW THE BUILD ANSWERS EACH ────────────────────────────────────────────
 *
 * BR-ADM-03-01 and BR-ADM-03-02 are ONE MECHANISM, not two. The dry run is
 * the real import, executed against the real services in the real
 * transaction, and then ROLLED BACK. There is no second, weaker validation
 * path that could disagree with the apply — "how many rows will be added, how
 * many changed" are counted by adding and changing them. Confirming replays
 * the identical code with the commit left in.
 *
 * BR-ADM-03-03 is the FAILED run's report: `problems`, each naming the row and
 * the column, all of them at once. Because the import is strictly
 * all-or-nothing, a refused row refuses the file — so "the rows that were
 * refused" is the whole set, and the amber `warnings` status now means what §7
 * says it means for a run that LANDED: the services had something to say
 * (a duplicate partner name, a tier price above the unit price) about rows
 * that all went in. `failed` is EC-15-18's missing status and is where a file
 * with a bad row lands.
 *
 * BR-ADM-03-04 is satisfied by the rule under everything: the import calls the
 * services, and the services already refuse to move an item's stock unit once
 * it has moved (BR-MD-03-04) or a partner's currency or opening balance once
 * a document exists (BR-MD-01-05/-06). An import cannot reach past a lock that
 * a screen cannot reach past, because it is the same code.
 *
 * BR-ADM-03-05 belongs to `opening_balances`, which is NOT IMPORTABLE here —
 * see `IMPORTABLE_DATA_TYPES`.
 *
 * §15's "two users at once" is a transaction-scoped advisory lock on
 * (tenant, data type), taken with `try` so the second caller is TOLD rather
 * than queued behind a fifty-thousand-row transaction.
 *
 * ── WHAT THE EXPORT HALF HAS TO GET RIGHT ─────────────────────────────────
 *
 * BR-ADM-03-08 is the rule that matters: "An export must contain EXACTLY what
 * the person running it is allowed to see. An export must never be a way
 * around a permission." Two consequences, both built:
 *
 *   a data type the caller may not read is NOT OFFERED at all (ERR-ADM-03-05:
 *     "The data type is not offered at all"), which is why `options` exists
 *     rather than the client listing the six from a constant; and
 *   a COLUMN the caller may not see is absent from the sheet rather than
 *     blanked in it — `inventory.see_item_cost` gates cost and margin exactly
 *     as MD-03 §9 gates them on screen, and the read never selects the column.
 */
// ── The six data types (§5) ─────────────────────────────────────────────────
/**
 * "The six data types, the same on both sides: Items, Customers, Suppliers,
 * Chart of Accounts, Opening Balances, Stock Balances."
 *
 * §5 also draws the line the build keeps: "you cannot import or export
 * TRANSACTIONS of any kind — no invoices, no bills, no journal entries, no
 * stock movements. The six are all master data plus opening figures. That is a
 * deliberate and sensible line, and a build must decide whether to keep it."
 *
 * DECIDED: kept, and for a reason stronger than the spec's own. A transaction
 * export is a REPORT, and DSH-05..DSH-08 already export every one of them with
 * the columns and filters the reader chose (BR-DSH-10-24). A second path to
 * the same rows would be a second definition of what "posted" means and a
 * second place for R9 to be got wrong.
 */
export const DATA_TYPES = [
    'items',
    'customers',
    'suppliers',
    'chart_of_accounts',
    'opening_balances',
    'stock_balances',
];
export const DataTypeSchema = z.enum(DATA_TYPES);
/**
 * §5 field 3 — "XLSX, CSV, or PDF. Only on the export side."
 *
 * PDF IS DEFERRED, with a reason rather than an omission. `packages/documents`
 * renders PDFs through headless Chromium as a JOB (DECISION.md §2.13 — "PDF
 * generation should be a job, not a request", because a request thread waiting
 * on Chromium is one an invoice-heavy month can exhaust), and every template it
 * owns is a DOCUMENT — an invoice, a statement, a report grid. A master-data
 * dump is neither: §15 puts a 10 MB spreadsheet at "roughly 50,000 rows", and
 * a 50,000-row PDF is not a thing anybody wants and is minutes of Chromium.
 * XLSX and CSV are what "your accountant asks for everything in a file" means.
 *
 * ── AND IT IS NOW AUTHORISED, WHICH THE ARGUMENT ABOVE COULD NOT DO ────────
 *
 * The reasoning above is sound and was, until 27 Aug 2026, the only thing
 * standing behind a build that ships two of the three formats ADM-03 §5 names.
 * The PART 21 sweep found it that way: well argued in code, ratified by no
 * decision document — the same finding it made about the ADM-01/ADM-06 Save
 * button. An argument a wave finds persuasive is not a permission.
 *
 * Ruled in `DELEGATED RULINGS — 26 Aug 2026.md`, "Ratified in passing
 * (27 Aug)": *"the ADM-01/ADM-06 Save button deviation (against unretired
 * BR-ADM-01-13) and the PDF-export deviation (against ADM-03 §5) — both
 * argued in code, now authorised; the implementing wave adds the fixed-opening
 * notes."*
 *
 * WHAT IS AUTHORISED IS THE DEVIATION, NOT A RETIREMENT. ADM-03 §5 still says
 * "XLSX, CSV, or PDF" and carries no retirement notice, so none is claimed
 * here: the project's fixed opening, "Retired by delegated ruling", belongs on
 * a spec row the document is giving up (see D34 and the POS receipt buttons at
 * `apps/web/src/features/pos/pos-page.tsx`), and this row is not one. The
 * third format is DEFERRED under an authority, which is a different state from
 * both "unbuilt" and "withdrawn", and stating it as either would be false.
 *
 * REVERSIBLE, with the trigger named rather than left to judgement. PDF joins
 * `EXPORT_FORMATS` the day a master-data dump can be rendered WITHOUT a
 * request thread waiting on Chromium — a queued export whose result is
 * fetched, which is DECISION.md §2.13's own shape and the thing
 * `packages/documents` already does for documents. It does NOT come back by
 * adding the string here: a format in this list with no renderer behind it is
 * an option that fails when chosen, which is exactly what EC-19-59 forbids.
 */
export const EXPORT_FORMATS = ['xlsx', 'csv'];
export const ExportFormatSchema = z.enum(EXPORT_FORMATS);
/**
 * WHAT EACH DATA TYPE COSTS TO READ — BR-ADM-03-08's gate, and ERR-ADM-03-05's
 * "the data type is not offered at all".
 *
 * `admin` opens the SCREEN (BR-ADM-03-09/BR-PERM-05). It does not open the
 * DATA: an Administrator holds everything, but a custom role holding `admin`
 * and nothing else must not be able to download the chart of accounts from
 * here when it cannot open FIN-01. The area cited is the one PART 16.2.2 gives
 * the record's own screen.
 *
 *   items, stock_balances  → inventory (16.2.2 row 3)
 *   customers, suppliers   → crm       (row 5; MD-01 lives in the CRM area)
 *   chart_of_accounts,
 *   opening_balances       → accounting (row 6)
 */
export const DATA_TYPE_PERMISSION = {
    items: 'inventory.view',
    customers: 'crm.view',
    suppliers: 'crm.view',
    chart_of_accounts: 'accounting.view',
    opening_balances: 'accounting.view',
    stock_balances: 'inventory.view',
};
/**
 * COLUMNS THE READER MAY NOT SEE ARE ABSENT, NOT BLANK.
 *
 * MD-03 §9 and `SEE_ITEM_COST` settled this for the screen: "a number that
 * reaches the browser is a number the browser can be made to show", so the
 * read that is not entitled to a cost NEVER SELECTS THE COLUMN. A spreadsheet
 * is the same argument with a longer life — a file lands in an inbox and is
 * forwarded — so the same rule applies, and the column is missing from the
 * header row rather than emptied under it.
 */
export const COST_COLUMNS = {
    items: ['costPrice'],
    stock_balances: ['avgUnitCost', 'value'],
};
export const COST_PERMISSION = SEE_ITEM_COST;
// ── The import side ─────────────────────────────────────────────────────────
/**
 * WHICH OF THE SIX CAN BE IMPORTED — ALL SIX, and the two that POST arrived
 * in their own wave, which is what the paragraph below used to scope out.
 *
 * "Five of the six imports touch nothing. Items, customers, suppliers, and the
 * chart of accounts are MASTER DATA: they describe things, they do not move
 * money. Opening balances are the exception, AND THEY POST."
 *
 * The first import wave built the four that touch nothing and wrote the scope
 * for the two that post. Both are built now, and both kept the shape that
 * wave demanded rather than growing a second one:
 *
 *   opening_balances — §12 makes it a JOURNAL ENTRY that must balance, and
 *     BR-ADM-03-05 confines it to "an open period, and only before the first
 *     document is confirmed in that period". It posts ONE entry, through
 *     `@palaccount/ledger`'s `post()` like every other figure in the product,
 *     with 3040 Opening Balance Equity as §12's "whatever is left over"
 *     counter-leg. Balanced or refused; never part-loaded.
 *   stock_balances — §12: "stock balances post as well, because stock has a
 *     value… an import of stock balances outside the opening period must be a
 *     STOCK ADJUSTMENT WITH A REASON, not a silent rewrite." Built exactly
 *     that way: every quantity goes through `StockWriterService` (BR-INV-06-01
 *     — "there must be no other way"), as an INV-08 voucher whose reason is
 *     `opening_balance` inside the opening period and the person's own typed
 *     reason outside it. FIFO layers follow from the writer, so a FIFO
 *     warehouse is seeded with layers rather than a bare figure, and the
 *     warehouse's valuation method locks itself the moment the seed lands.
 *
 * A type absent from this list is NOT OFFERED on the import side and is
 * REFUSED if asked for anyway — the same two-level shape ERR-ADM-03-05 gives
 * the export, because a dropdown is not a control. The list is now the full
 * six, and the constant stays because the SHAPE of the rule outlives the
 * coincidence that nothing is currently excluded.
 */
export const IMPORTABLE_DATA_TYPES = [
    'items',
    'customers',
    'suppliers',
    'chart_of_accounts',
    'opening_balances',
    'stock_balances',
];
export function isImportable(dataType) {
    return IMPORTABLE_DATA_TYPES.includes(dataType);
}
/**
 * THE LINE §12 DRAWS, now that both sides of it are built: "five of the six
 * imports touch nothing… opening balances are the exception, AND THEY POST."
 *
 * It is not a cosmetic grouping. A posting type behaves differently in four
 * places, and every one of them is a rule rather than a convenience:
 *
 *   it needs a DATE (`asOf`), because §12's entry is one entry and BR-ADM-03-05
 *     is a question about one period;
 *   it is confined by BR-ADM-03-05, which the four master types are not;
 *   it costs a POSTING permission rather than a record's (see
 *     `DATA_TYPE_WRITE_PERMISSION`);
 *   its rows are applied as ONE act at the end rather than one act each — a
 *     balancing entry cannot be built a row at a time, which is the same
 *     reason §12 says an import that does not balance is refused rather than
 *     part-loaded.
 *
 * The screen reads this to decide whether to ask for a date; the server reads
 * it to decide which half of the import runs. One definition, so the two
 * cannot disagree about which types are dangerous.
 */
export const POSTING_DATA_TYPES = ['opening_balances', 'stock_balances'];
export function isPostingImport(dataType) {
    return POSTING_DATA_TYPES.includes(dataType);
}
/**
 * WHAT AN IMPORT COSTS — and it is NOT what an export costs.
 *
 * This is the whole reason the constant exists rather than reusing
 * `DATA_TYPE_PERMISSION`. BR-ADM-03-08 is written about exports and reads:
 * "an export must contain exactly what the person running it is allowed to
 * SEE." Its import counterpart is not a paraphrase — an import is a WRITE, and
 * a fence built for reads does not cover one. A role holding `inventory.view`
 * can legitimately download the whole catalogue; letting it upload one back
 * would be `inventory.create` and `inventory.edit` granted by the back door,
 * on the one screen whose stated purpose is moving a thousand records at once.
 *
 * So the gate is the WRITE permission of the record's own screen, and both
 * halves are required up front rather than per row: a file that adds and
 * changes is one action to the person running it, and being refused halfway
 * through the confirmation is exactly what BR-ADM-03-02 exists to prevent.
 */
/**
 * ── AND WHAT THE TWO POSTING TYPES COST, which is a harder question ───────
 *
 * The four above write a RECORD, so the gate is that record's screen. The two
 * below write a POSTING, and a posting is not a bigger record — it is a
 * different act, with a different screen behind it and a different permission
 * on that screen. So the gate is the UNION of what the same acts take when a
 * person does them one at a time, and nothing less:
 *
 *   opening_balances  accounting.create + accounting.edit + accounting.approve
 *                     — FIN-02's own three. An opening-balance import BUILDS a
 *                     journal entry (`accounting.create`), fills its lines
 *                     (`accounting.edit`) and POSTS it, and posting a manual
 *                     journal is `accounting.approve` on FIN-02's own route.
 *                     A gate of create+edit alone would let a role that may
 *                     draft a journal but not post one post fifty of them
 *                     through this screen, which is the back door in its
 *                     purest form.
 *                     + crm.edit — because the file also moves
 *                     `partners.opening_balance`, which is MD-01 field 27 and
 *                     takes `crm.edit` on the partner's own card. Writing the
 *                     ledger and not the subledger would leave 1121 disagreeing
 *                     with the sum of the customer balances, so BOTH move, and
 *                     therefore both are paid for.
 *                     + banking.edit — the same statement for a bank account's
 *                     opening balance (FIN-09 field 11).
 *
 *   stock_balances    inventory.create + inventory.edit + inventory.approve —
 *                     INV-08's own three, in the same reading: the import
 *                     raises a Stock In/Out voucher and posts it, and INV-08's
 *                     post route is `inventory.approve` (AP-6, "posting is the
 *                     confirming act").
 *
 * THE TEST THAT MATTERS: `admin.view` — which opens this SCREEN under
 * BR-ADM-03-09 — must get nothing. It does: it appears in neither list, and
 * neither does `accounting.view`, which is what the EXPORT side charges for
 * the same two types. A role that may download the opening balances and may
 * not post a journal can read the file and cannot upload one back, which is
 * the whole distinction between BR-ADM-03-08 and its write counterpart.
 *
 * WHO ACTUALLY HOLDS THESE (16.4.2, checked rather than assumed): the Owner
 * (the flag, BR-PERM-66) and the Administrator hold both sets. The ACCOUNTANT
 * holds `accounting` RCUA, `crm` RCU and `banking` RCUA — every permission in
 * the opening-balance set — and holds `inventory` R only, so they may load the
 * company's opening figures and may not rewrite the warehouse. That is the
 * right division and it falls out of the rule rather than being arranged: the
 * books are the accountant's, the shelves are not.
 */
export const DATA_TYPE_WRITE_PERMISSION = {
    items: ['inventory.create', 'inventory.edit'],
    customers: ['crm.create', 'crm.edit'],
    suppliers: ['crm.create', 'crm.edit'],
    chart_of_accounts: ['accounting.create', 'accounting.edit'],
    opening_balances: [
        'accounting.create',
        'accounting.edit',
        'accounting.approve',
        'crm.edit',
        'banking.edit',
    ],
    stock_balances: ['inventory.create', 'inventory.edit', 'inventory.approve'],
};
/**
 * ═════════════════════════════════════════════════════════════════════════
 * THE CEILING — 50,000, REACHED BY CHUNKING, AND WHY NOT THE OTHER WAY.
 * ═════════════════════════════════════════════════════════════════════════
 *
 * §15's "Very large" says "a 10 MB spreadsheet is roughly 50,000 rows", and
 * BR-ADM-03-10 says a file ABOVE 10 MB must be refused with a clear message —
 * which implies one at or under it is accepted. The first import wave could
 * not reach 50,000 in one upload and named the two ways out. This is the
 * decision between them, with the argument, because it is the kind of choice
 * that looks like a preference and is not.
 *
 * THE CONSTRAINT: the parsed grid travels as the request BODY, and Fastify's
 * body limit is 1 MiB (`main.ts` does not raise it, deliberately — a body
 * limit is a denial-of-service control). A master-data row serialises to
 * roughly 150-400 bytes, so one request carries a few thousand rows.
 *
 * OPTION A — a per-route body limit on this endpoint alone. REJECTED, three
 * reasons, and the third is the one that decides it:
 *
 *   1. It does not remove the ceiling, it moves it. 50,000 rows is ~15 MB of
 *      JSON that Fastify must buffer WHOLE, in the API process, before a line
 *      of it is parsed — per concurrent import. The 1 MiB limit exists to stop
 *      exactly that, so this trades a memory-shaped denial of service for a
 *      row count, on the one route whose stated purpose is being large.
 *   2. Fastify's per-route `bodyLimit` is not reachable through the ts-rest /
 *      Nest adapter without dropping to the raw Fastify instance and
 *      re-registering the route beside the one Nest owns — two definitions of
 *      one endpoint, which is how a validation layer gets skipped later.
 *   3. IT ANSWERS NEITHER HALF OF §15. "The import must show PROGRESS and must
 *      not appear FROZEN" is the other sentence in the same bullet, and one
 *      very large request is precisely a frozen tab with a spinner on it.
 *
 * OPTION B — CHOSEN: a chunked upload into a staging table, drained by the
 * one transaction that already exists.
 *
 *   - Each chunk is an ordinary small request under the ordinary body limit,
 *     so nothing about the platform's DoS posture changes.
 *   - PROGRESS IS REAL AND IT IS THE SERVER'S. Every chunk's reply carries
 *     `received` out of `total` counted by `SELECT count(*)` over the staged
 *     rows — not a byte counter in the browser, not a spinner renamed. The
 *     screen shows a number the server produced.
 *   - ALL-OR-NOTHING IS NOT WEAKENED, IT IS EASIER TO HOLD. Staging is not
 *     importing: staged rows are the FILE, sitting in a table instead of in a
 *     request body, and NOTHING is applied until every chunk has landed and
 *     the person has said go. The apply is the same single transaction the
 *     inline path uses, reading its rows from the staging table instead of
 *     from the body — the dry-run-IS-the-import mechanism EXTENDED, not
 *     forked. A kill at any point during the upload leaves staged rows and
 *     zero applied rows, which the suite asserts by killing one.
 *   - A staging run is scoped to its uploader and expires (`expires_at`), so
 *     an abandoned upload is garbage rather than a half-import waiting to
 *     happen.
 *
 * WHAT THE TWO CONSTANTS MEAN NOW:
 *   `MAX_IMPORT_ROWS`        one REQUEST's worth — the inline path's whole
 *                            file, and a chunk's size on the staged path.
 *   `MAX_STAGED_IMPORT_ROWS` one IMPORT's worth, and it is §15's own figure.
 *
 * The small path is untouched: 5,000 covers §1's example ("load A THOUSAND
 * ITEMS") and §13's 400-row walkthrough in one request with no staging table
 * and no second round trip, and a person importing 400 rows should not pay for
 * a mechanism built for 50,000.
 */
export const MAX_IMPORT_ROWS = 5_000;
/**
 * §15's "a 10 MB spreadsheet is roughly 50,000 rows" — the ceiling on ONE
 * import, however many chunks it arrives in. Above this the file is REFUSED
 * with a sentence (`tooManyRows`), never truncated: BR-ADM-03-02 again, and
 * the refusal is checked in the browser before the first chunk is sent and on
 * the server when the run is opened, so a client that lies is still refused.
 */
export const MAX_STAGED_IMPORT_ROWS = 50_000;
/**
 * What one request body can carry, with margin under Fastify's 1 MiB.
 *
 * Checked in the BROWSER, on the serialised payload, so a wide file with long
 * names is refused with a sentence rather than by the platform with a 413 the
 * screen cannot explain.
 */
export const MAX_IMPORT_BODY_BYTES = 900_000;
/** BR-ADM-03-10's own figure, enforced in the browser where the bytes are. */
export const MAX_IMPORT_BYTES = 10 * 1024 * 1024;
// ── Wire shapes ─────────────────────────────────────────────────────────────
/**
 * One column of a template or an export.
 *
 * `key` is the stable machine name — what a future import will match a
 * spreadsheet's headings against (ERR-ADM-03-01: "Column 3 should be `SKU`").
 * `label` is what the sheet actually shows, and it is TRANSLATED by the client
 * from `impexp.col.<key>`, so an Arabic user's template is Arabic. The two are
 * separate on purpose: a heading a translator improves must never change what
 * an import will accept.
 */
export const TransferColumnSchema = z
    .object({
    key: z.string(),
    /** 'text' · 'number' · 'date' — what the xlsx writer must make of it. */
    kind: z.enum(['text', 'number', 'date']),
    /** True on the columns a future import will refuse a file without. */
    required: z.boolean(),
})
    .strict();
export const TransferGridSchema = z
    .object({
    dataType: DataTypeSchema,
    columns: TransferColumnSchema.array(),
    /**
     * Every cell as a STRING, in `columns` order. Money arrives as its decimal
     * string and never as a JS number (§2.1) — the xlsx writer puts the string
     * straight into the XML, so no float touches an amount on the way to a
     * sheet the accountant will sum.
     */
    rows: z.string().array().array(),
})
    .strict();
export const TransferRunSchema = z
    .object({
    id: z.uuid(),
    direction: z.enum(['import', 'export']),
    dataType: DataTypeSchema,
    format: z.enum(['xlsx', 'csv', 'pdf']).nullable(),
    status: z.enum(['in_progress', 'imported', 'warnings', 'failed', 'exported']),
    rowCount: z.number().int().min(0),
    /** BR-ADM-03-07 — WHO. Null when they have left; the run stays. */
    ranBy: z.string().nullable(),
    ranAt: z.iso.datetime(),
    note: z.string().nullable(),
})
    .strict();
export const TransferOptionsSchema = z
    .object({
    /**
     * ERR-ADM-03-05 — only the types this caller may read. The client draws
     * the dropdown from this and from nothing else, so a type they cannot see
     * is absent rather than refused on submit.
     */
    dataTypes: DataTypeSchema.array(),
    /**
     * ERR-ADM-03-05's shape again, for the WRITE side — the types this caller
     * may import. A different list from `dataTypes` for two reasons, and the
     * screen must not conflate them: only four of the six are importable at
     * all, and importing takes create+edit where exporting takes view. A role
     * that may download the catalogue and not change it sees a full export
     * dropdown and an empty import one, which is the truth.
     */
    importDataTypes: DataTypeSchema.array(),
    /** §4's two lists, newest first. */
    imports: TransferRunSchema.array(),
    exports: TransferRunSchema.array(),
})
    .strict();
/** Named for its screen: PART 22's tenant export already owns `ExportRequestSchema`. */
export const DataExportRequestSchema = z
    .object({ dataType: DataTypeSchema, format: ExportFormatSchema })
    .strict();
// ── The import's wire shapes ────────────────────────────────────────────────
/**
 * ERR-ADM-03-04's two answers — "128 of these 128 items already exist. Do you
 * want to UPDATE them, or STOP?"
 *
 * `stop` is the default, and the default matters: the destructive reading of a
 * re-uploaded file is the one that must be asked for. §15's Duplicate row —
 * "importing the same file twice must ask whether to update or stop, NEVER
 * SILENTLY DOUBLE UP" — is why neither answer is "insert them again".
 */
export const IMPORT_DUPLICATE_MODES = ['stop', 'update'];
export const ImportDuplicateModeSchema = z.enum(IMPORT_DUPLICATE_MODES);
/**
 * ONE PROBLEM, NAMED WHERE IT IS.
 *
 * ERR-ADM-03-01 quotes a column POSITION — "Column 3 should be `SKU`" — so the
 * report speaks in the same terms the template does: the row as the person
 * sees it in the spreadsheet (the header is row 1, so the first record is row
 * 2) and the column both by its stable key and by its 1-based position.
 *
 * A person fixing a 500-row file one error per attempt is a worse product than
 * one told what is wrong with all 500, so these arrive as a LIST and the
 * validation never stops at the first.
 */
export const ImportProblemSchema = z
    .object({
    /** The spreadsheet's own row number. Null for a problem about the file. */
    row: z.number().int().min(1).nullable(),
    /** The stable column key (ERR-ADM-03-01's `SKU`). Null for a whole-row problem. */
    column: z.string().nullable(),
    /** 1-based, as ERR-ADM-03-01 counts. Null when the column is not a position. */
    columnPosition: z.number().int().min(1).nullable(),
    /**
     * The sentence. For a row that breaks a business rule this is the SAME
     * sentence the screen shows, because it came from the same service.
     */
    message: z.string(),
})
    .strict();
export const ImportRequestSchema = z
    .object({
    dataType: DataTypeSchema,
    /**
     * The file's header row, resolved to COLUMN KEYS.
     *
     * The client does the resolving, and that is the same split the export
     * uses rather than a new one: the sheet's headings are TRANSLATED
     * (`impexp.col.<key>`, so an Arabic user's template is Arabic), and the
     * translation table is the client's. What the server owns is what the
     * column list must BE, and it re-checks every entry — a heading that
     * matched nothing arrives verbatim and is refused by position.
     */
    columns: z.string().array().max(200),
    /**
     * Every cell as a string, in `columns` order — the same shape an export
     * returns.
     *
     * DELIBERATELY UNBOUNDED HERE. `MAX_IMPORT_ROWS` is enforced in the
     * service instead, because a zod `.max()` comes back as a validation
     * error naming a path, and the person holding a 6,000-row spreadsheet
     * needs the sentence that tells them to split it. A ceiling worth having
     * is a ceiling worth explaining.
     */
    rows: z.string().array().array(),
    /**
     * THE STAGED FILE, instead of `rows` — see `MAX_STAGED_IMPORT_ROWS` for
     * the argument. When this is set the server reads the rows out of the
     * staging table the chunks landed in, and `rows` must be empty: two
     * sources for one file is how a confirmation ends up applying something
     * other than what was checked.
     */
    uploadId: z.uuid().nullable().default(null),
    onDuplicate: ImportDuplicateModeSchema.default('stop'),
    /**
     * THE DATE THE OPENING FIGURES ARE AS AT — required by the two types that
     * POST, refused on the four that do not.
     *
     * It is not a column, and that is deliberate: §12's entry is ONE entry, so
     * it has ONE date, and a date per row would be an invitation to file half
     * a company's opening position in June and half in July. It is also the
     * date BR-ADM-03-05 is asked about ("the accounting period is open, and …
     * before the first document is confirmed in that period"), and a rule
     * about a period cannot be evaluated against fifty thousand different
     * answers.
     */
    asOf: IsoDateSchema.optional(),
    /**
     * §12: "an import of stock balances outside the opening period must be a
     * STOCK ADJUSTMENT WITH A REASON, not a silent rewrite."
     *
     * So a stock-balance import carries INV-08's own reason, and the two cases
     * are one mechanism: inside the opening period the reason is
     * `opening_balance` (INV-08 §5.2's own "first loading of stock into a new
     * system", which posts to 3040 — §12's stock figure landing in the
     * balancing entry), and outside it the person must name a real reason,
     * which is what makes the rewrite a document instead of a silence.
     */
    reason: AdjustmentReasonSchema.optional(),
    /**
     * BR-ADM-03-01 — "must WAIT for the user to say go".
     *
     * False is the dry run: the whole import happens and is then rolled back,
     * so the counts are counted rather than predicted. True commits it.
     */
    confirm: z.boolean().default(false),
})
    .strict()
    .refine((body) => body.uploadId === null || body.rows.length === 0, {
    message: 'An import carries either its rows or an upload, never both.',
    path: ['rows'],
});
// ── The chunked upload (§15's "very large", and its progress) ───────────────
/**
 * OPENING a staged upload: the columns and the row count are declared FIRST,
 * so both can be refused before a single row travels.
 *
 * The count is a promise the server holds the person to — the apply refuses a
 * run whose staged rows do not equal what was declared, which is how a chunk
 * lost in flight becomes a refusal rather than a short import.
 */
export const BeginUploadRequestSchema = z
    .object({
    dataType: DataTypeSchema,
    columns: z.string().array().max(200),
    totalRows: z.number().int().min(1).max(MAX_STAGED_IMPORT_ROWS),
})
    .strict();
/**
 * ONE CHUNK, and §15's progress in its reply.
 *
 * `firstRow` is the spreadsheet's own row number for `rows[0]` (the header is
 * row 1, so the first record is 2) — carried rather than inferred, so a chunk
 * that arrives out of order still reports its faults against the row the
 * person can scroll to, and a chunk sent twice overwrites itself instead of
 * doubling the file.
 */
export const UploadChunkRequestSchema = z
    .object({
    uploadId: z.uuid(),
    firstRow: z.number().int().min(2),
    rows: z.string().array().array(),
})
    .strict();
/**
 * §15: "the import must show PROGRESS and must not appear frozen."
 *
 * Both figures are the SERVER's: `received` is `count(*)` over the staged
 * rows, `total` is what was declared when the run was opened. The screen shows
 * a fraction it was told, never one it computed from how many requests it has
 * managed to send — those differ exactly when something has gone wrong, which
 * is when a progress indicator is worth having.
 */
export const UploadProgressSchema = z
    .object({
    uploadId: z.uuid(),
    received: z.number().int().min(0),
    total: z.number().int().min(0),
    complete: z.boolean(),
})
    .strict();
export const ImportResultSchema = z
    .object({
    dataType: DataTypeSchema,
    /** False on a dry run and on a refusal — in both, nothing was written. */
    applied: z.boolean(),
    /** BR-ADM-03-01's three counts, in its own words. */
    added: z.number().int().min(0),
    changed: z.number().int().min(0),
    refused: z.number().int().min(0),
    /** BR-ADM-03-03. Empty means every row is acceptable. */
    problems: ImportProblemSchema.array(),
    /**
     * Non-blocking remarks the services returned about rows that ARE going in
     * — a duplicate partner name, a tier price above the unit price. They are
     * what makes a landed run amber (§7's "With warnings") rather than green.
     */
    warnings: ImportProblemSchema.array(),
    /** The history row, when one was written. */
    runId: z.uuid().nullable(),
})
    .strict();
export const DATA_TRANSFER_MESSAGES = {
    /** BR-ADM-03-08 / ERR-ADM-03-05, for the caller who guesses a type anyway. */
    typeNotAllowed: 'You do not have permission to export that data.',
    /** §15's Empty row: both sentences "already exist in the product's translations and are never shown". */
    noImports: 'No imports yet.',
    noExports: 'No exports yet.',
    /**
     * §5's line, said on the card the drop zone is on. The two types that POST
     * ARE importable now, and the sentence says what that costs rather than
     * disappearing: they write to the accounts, so they need a date and they
     * meet BR-ADM-03-05.
     */
    importPostingTypes: 'Opening balances and stock balances post to the accounts. They need a date, they must balance, and they can only go into an open period that has no confirmed documents yet.',
    // ── The import's own sentences ────────────────────────────────────────────
    /** BR-ADM-03-08's write half — see `DATA_TYPE_WRITE_PERMISSION`. */
    importNotAllowed: 'You do not have permission to import that data.',
    /** The two posting types, refused by name rather than by silence. */
    importTypeNotSupported: (label) => `${label} cannot be imported. Only master data can be imported here.`,
    /**
     * ERR-ADM-03-01, to the letter — "This file does not match the Items
     * template. Column 3 should be `SKU`. Download the template and try again."
     * The label is the data type's; the key is the stable column name, which is
     * what the person can compare against the template they downloaded.
     */
    wrongColumns: (label, position, key) => `This file does not match the ${label} template. Column ${position} should be \`${key}\`. Download the template and try again.`,
    wrongColumnCount: (label, expected, found) => `This file does not match the ${label} template. It should have ${expected} columns and has ${found}. Download the template and try again.`,
    /**
     * The COST-WRITE FENCE, and it is the interesting one. A role that cannot
     * SEE a cost gets a template and an export with no cost column in them
     * (`COST_COLUMNS`); a file that carries one anyway is that role trying to
     * WRITE the figure it may not read, so the file is refused by column and by
     * position rather than the column being quietly ignored — ignoring it would
     * import a thousand items at cost zero and look like it worked.
     */
    costColumnNotAllowed: (position, key) => `Column ${position} (\`${key}\`) is a cost, and you do not have permission to see or set costs. Remove the column and try again.`,
    /** §15's Zero — "a file with headings and no rows must be refused with a clear message". */
    emptyFile: 'That file has column headings and no rows. There is nothing to import.',
    /**
     * The ceiling, said in the unit the server sees. See `MAX_IMPORT_ROWS` for
     * why it is what it is and what would have to change to raise it — the one
     * thing this build will not do is import the first few thousand rows and
     * call it done.
     */
    tooManyRows: (max) => `That file has more than ${max.toLocaleString('en-US')} rows, which is more than one import can carry. Split it into smaller files.`,
    /** BR-ADM-03-10, in the unit the browser sees. */
    tooLarge: 'That file is larger than 10 MB. Split it into smaller files.',
    /** §15's "Two users at once" — the second is TOLD, not queued. */
    alreadyRunning: 'Somebody else is importing this data type right now. Wait for their import to finish and try again.',
    /**
     * ERR-ADM-03-04 — "128 of these 128 items already exist. Do you want to
     * update them, or stop?" A question, so it is a blocking problem on the dry
     * run that the person answers by choosing Update, never a silent decision.
     */
    duplicates: (existing, total, label) => `${existing} of these ${total} ${label} already exist. Choose Update to change them, or Stop.`,
    /**
     * A file that names the same record twice.
     *
     * On a MASTER type this is ERR-ADM-03-04's question ("already exist — update
     * or stop?"), because the second row is an edit of what the first made. On a
     * POSTING type it cannot be: two opening balances for one customer is not an
     * update, it is a file that contradicts itself, and quietly taking the last
     * one would post whichever figure happened to be lower down the sheet.
     */
    repeatedRow: (first) => `This row names the same record as row ${first}. Each record can appear only once in an opening-balance file.`,
    /** A row that is short of the header, before any field is even looked at. */
    shortRow: (expected) => `This row has fewer than ${expected} columns.`,
    required: 'This column is required and is empty.',
    /** §15's Rounding — "money must be read to 2 decimals and REFUSED if it has more". */
    notMoney: 'This is not an amount. Use digits and at most 2 decimals, like 1250.00.',
    /**
     * The same rule where the figure is a UNIT COST rather than an amount.
     *
     * §15's "2 decimals" is about MONEY — the amounts that land on a document and
     * are summed by an accountant (N-1). A unit cost is held to six
     * (`numeric(18,6)`) everywhere in this product, because the cost of one
     * screw is not a two-decimal quantity, and the stock export writes six. A
     * flat 2-decimal check here would refuse the product's own export file,
     * which is the one file an import must always accept.
     */
    notMoneyPrecision: (decimals) => `This is not an amount. Use digits and at most ${decimals} decimals.`,
    notNumber: 'This is not a number.',
    notOneOf: (allowed) => `This must be one of: ${allowed.join(', ')}.`,
    /**
     * §15's "Deleted linked record" — "importing an item pointing at a category
     * that does not exist must either make the category or refuse the row.
     * PROPOSED: REFUSE THE ROW AND SAY WHICH CATEGORY WAS MISSING." Which is why
     * the sheet carries a name rather than a uuid.
     */
    unknownReference: (what, value) => `There is no ${what} called “${value}”.`,
    /** The header on a whole file that failed, for the history row's note. */
    failedNote: (problems) => `Refused: ${problems} ${problems === 1 ? 'problem' : 'problems'}. Nothing was imported.`,
    // ── The two types that POST ───────────────────────────────────────────────
    /**
     * The date §12's one entry is dated, asked for rather than defaulted. A
     * default would be today's date, and today is exactly the wrong answer for a
     * figure whose whole purpose is to be dated before the company started
     * trading in the system.
     */
    dateRequired: 'Pick the date these opening figures are as at.',
    dateNotUsed: 'This data type does not take a date.',
    reasonNotUsed: 'Only a stock-balance import takes a reason.',
    /**
     * ERR-ADM-03-03, to the letter — "This period already has confirmed
     * documents. Opening balances can only be imported into an empty period."
     * BR-ADM-03-05's second half, and the first enforcement of it anywhere.
     */
    periodHasDocuments: (period) => `${period} already has confirmed documents. Opening balances can only be imported into an empty period.`,
    /**
     * §15's "Closed period" row, in GEN-92's own voice — the same sentence a
     * posting gets anywhere else, because it is the same rule.
     */
    periodClosed: (period) => `${period} is closed. Pick a date in an open period, or ask your accountant to reopen it.`,
    /**
     * §12: "an opening-balance import that does not balance must be REFUSED,
     * NOT PART-LOADED — which is the real reason for the all-or-nothing rule."
     *
     * THE ENGINE'S REFUSAL, MADE READABLE — and it is wired rather than
     * decorative. The balancing figure IS the counter-leg, so this should be
     * unreachable: whatever the assets and liabilities leave over goes to 3040
     * by construction. But "should be unreachable" is a claim about code that
     * changes, and the alternative to catching `UnbalancedEntryError` here is a
     * 500 on the one screen where a person is loading their company's whole
     * opening position. So the import catches it and reports it as a fault of
     * the FILE, with the two figures in it.
     */
    postingRefused: (reason) => `These opening figures could not be posted: ${reason}`,
    /** §15's Foreign currency row — "must state which currency each figure is in". */
    currencyMismatch: (name, held, given) => `${name} is kept in ${held}, and this row says ${given}. Change the row, or change the record first.`,
    /** Every row restates what the record already holds — see the delta arithmetic. */
    nothingToPost: 'Every row in this file leaves the figure exactly as it already is. There is nothing to import.',
    /**
     * §12's outside-the-opening-period rule, said as a requirement rather than
     * discovered as a rewrite: a reason is what turns a bulk overwrite into a
     * document somebody can read later.
     */
    reasonRequired: 'This period already has stock movements, so these figures are an adjustment. Pick the reason they are changing.',
    /**
     * A reason belongs to a direction (INV-08 §5.2/§5.3). A file that moves
     * stock both ways under a one-way reason is refused ROW BY ROW, naming the
     * rows, rather than silently splitting into two vouchers whose reasons the
     * person never chose.
     */
    reasonWrongDirection: (reason) => `This row lowers the quantity, and “${reason}” is a reason for raising it. Split the file, or pick a reason that covers both.`,
    // ── The chunked upload (§15's "very large") ───────────────────────────────
    /** A staged run that has gone, expired, or belongs to somebody else. */
    uploadNotFound: 'That upload is no longer available. It may have expired. Choose the file again.',
    /**
     * The declared count and the staged count disagree — a chunk was lost. The
     * refusal is the whole point: the alternative is importing the rows that DID
     * arrive, which is BR-ADM-03-02's prohibition arriving by accident.
     */
    uploadIncomplete: (received, total) => `Only ${received.toLocaleString('en-US')} of ${total.toLocaleString('en-US')} rows reached the server. Nothing was imported. Upload the file again.`,
    /** A chunk arriving after the run was applied, or against a finished run. */
    uploadClosed: 'That upload has already been used. Choose the file again.',
};
export const dataTransferContract = c.router({
    options: {
        method: 'GET',
        path: '/api/data-transfer',
        responses: { 200: TransferOptionsSchema, 401: MessageSchema, 403: MessageSchema },
        summary: 'ADM-03 §4: the data types this caller may move (BR-ADM-03-08) and both history lists (BR-ADM-03-07).',
    },
    /**
     * §6 — "Downloads an empty spreadsheet with the right column headings for
     * the chosen data type. IN THE PROTOTYPE IT DOES NOTHING." It does
     * something now, and it is the same column definition the export uses, so
     * a template and an export of the same type can never disagree about what
     * a column is called.
     */
    template: {
        method: 'GET',
        path: '/api/data-transfer/template',
        query: z.object({ dataType: DataTypeSchema }).strict(),
        responses: { 200: TransferGridSchema, 401: MessageSchema, 403: MessageSchema },
        summary: 'ADM-03 §6: the empty template for a data type — headings only.',
    },
    /**
     * POST for what reads like a read, and for the same reason the audit
     * export is a POST: it WRITES — an audit row (BR-ADM-03-06, "exporting is
     * itself one of the eight audited actions") and a history row
     * (BR-ADM-03-07). A GET with side effects is one any prefetcher may fire.
     */
    exportData: {
        method: 'POST',
        path: '/api/data-transfer/export',
        body: DataExportRequestSchema,
        responses: {
            200: TransferGridSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'ADM-03 §6: the rows, permission-scoped (BR-ADM-03-08). Writes its own audit and history rows.',
    },
    /**
     * §6's Upload file, and BOTH HALVES OF IT — the dry run and the go — are
     * this one route with `confirm` flipped.
     *
     * ONE ROUTE ON PURPOSE. Two would be two code paths, and BR-ADM-03-01's
     * whole value is that what the person was shown is what will happen; a
     * preview endpoint that validated and an apply endpoint that wrote would
     * be free to disagree, and the day they did the user would have approved
     * something else. Here the preview IS the apply, rolled back.
     *
     * It also means the file is uploaded twice for a confirmed import, which
     * is the cost of not holding fifty thousand parsed rows in server memory
     * between two requests keyed by a token — a staging table nobody asked
     * for, or a cache with an eviction bug that loses somebody's import.
     * The client already holds the parsed file; it sends it again.
     *
     * 200 either way, because a refused FILE is an answer and not a protocol
     * error: the body carries every problem, which is the point. 400/403/409
     * are for the things that are not about rows at all — the wrong columns,
     * the wrong permission, somebody else's import already running.
     */
    importData: {
        method: 'POST',
        path: '/api/data-transfer/import',
        body: ImportRequestSchema,
        responses: {
            200: ImportResultSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'ADM-03 §10: the import. `confirm: false` runs it and rolls back (BR-ADM-03-01); `confirm: true` commits it, all or nothing (BR-ADM-03-02).',
    },
    /**
     * §15's "very large" — open a staged upload. See `MAX_STAGED_IMPORT_ROWS`
     * for why chunking beat a per-route body limit.
     *
     * Opening a run writes NOTHING a person could mistake for an import: it is
     * a place to put a file, and the import is still the one route above,
     * still with `confirm` deciding whether the transaction commits.
     */
    beginUpload: {
        method: 'POST',
        path: '/api/data-transfer/upload',
        body: BeginUploadRequestSchema,
        responses: {
            201: UploadProgressSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'ADM-03 §15: opens a chunked upload for a file too large for one request.',
    },
    /**
     * One chunk in, the server's own count back — §15's "must show progress
     * and must not appear frozen", answered with a number the server produced.
     */
    uploadChunk: {
        method: 'POST',
        path: '/api/data-transfer/upload/chunk',
        body: UploadChunkRequestSchema,
        responses: {
            200: UploadProgressSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'ADM-03 §15: stages one chunk and reports rows received out of rows expected.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=data-transfer.js.map